// src/components/tour/tours/deviceManager.jsx
//
// Device Manager tour. ctx comes from usePageTour('device-manager', ...)
// in DeviceManager.jsx and holds the REAL page data.
//
// Badges/dots in the text reuse the page's real classes (cm-status-chip,
// dm-device-dot, dm-device-tag) so they look exactly like the UI.

import React from 'react';
import { replayStep } from './common';

export function getDeviceManagerSteps(ctx, env) {
    const {
        configuredDevices = [],
        orphanedDevices = [],
        deviceAlertStatus = {},
        visibleDevices = [],
        openMobileAddPanel,
        closeMobileAddPanel,
    } = ctx;
    const { isMobile } = env;

    const total = configuredDevices.length;
    const archived = orphanedDevices.length;
    const suppressed = configuredDevices.filter((d) => deviceAlertStatus[d.serial_number]).length;
    const first = visibleDevices[0];
    const firstName = first ? (first.device_name || 'Unnamed Device') : null;
    const firstMuted = first ? !!deviceAlertStatus[first.serial_number] : false;

    const dots = (
        <>
            <span className="dm-device-dot online" /> means online,{' '}
            <span className="dm-device-dot offline" /> offline
        </>
    );

    return [
        {
            target: null,
            title: 'Welcome to Device Manager',
            content: (
                <>
                    <p>
                        This is where you choose which Peplink routers are monitored.
                        You currently have <span className="pt-data">{total}</span> configured
                        device{total === 1 ? '' : 's'}
                        {suppressed > 0 && <> (<span className="pt-data">{suppressed}</span> with alerts muted)</>}.
                    </p>
                    <p>We'll walk through the page using your live data. Nothing is changed during the tour.</p>
                </>
            ),
        },

        // ---- Adding a device: desktop (form always visible) ----
        {
            layout: 'desktop',
            target: '[data-tour="dm-org"]',
            title: 'Step 1: pick an organization',
            content: <p>Adding a device starts here. Choose the Peplink organization that owns the router, and its device list loads in the next field.</p>,
        },
        {
            layout: 'desktop',
            target: '[data-tour="dm-device"]',
            title: 'Step 2: find the device',
            content: (
                <p>
                    Search by name or serial number. {dots}. Devices already being monitored
                    show <span className="dm-device-tag">Already Added</span> and can't be picked twice.
                </p>
            ),
        },
        {
            layout: 'desktop',
            target: '[data-tour="dm-add-btn"]',
            title: 'Step 3: add it',
            content: <p>Starts monitoring the selected device. It will appear in the list below.</p>,
        },
        {
            layout: 'desktop',
            target: '[data-tour="dm-bulk-btn"]',
            title: 'Bulk add',
            content: <p>Adding a whole fleet? Bulk add lists every device in the organization so you can tick many at once, or just the online ones.</p>,
        },

        // ---- Adding a device: mobile (behind a toggle) ----
        {
            layout: 'mobile',
            target: '[data-tour="dm-mobile-add-toggle"]',
            title: 'Add a device',
            onEnter: closeMobileAddPanel,
            content: <p>Tap <b>+ Add device</b> to open the add form. It stays hidden to keep your list on screen.</p>,
        },
        {
            layout: 'mobile',
            target: '[data-tour="dm-add-form"]',
            title: 'The add form',
            onEnter: openMobileAddPanel,
            content: (
                <>
                    <p>1. Pick the <b>organization</b> the router belongs to.</p>
                    <p>
                        2. Search the <b>device</b> by name or serial. {dots}. Devices already monitored
                        show <span className="dm-device-tag">Already Added</span>.
                    </p>
                    <p>3. Tap <b>Add device</b>, or use <b>Bulk add</b> to add many at once.</p>
                </>
            ),
        },

        // ---- Shared ----
        {
            target: '[data-tour="dm-configured-header"]',
            title: 'Configured devices',
            onEnter: isMobile ? closeMobileAddPanel : undefined,
            content: (
                <p>
                    Every device being monitored right now. The counter shows <b>shown / total</b>, and
                    the search box filters the list by device name or serial number.
                </p>
            ),
        },
        {
            target: '[data-tour="dm-first-device"]',
            title: firstName ? `Meet “${firstName}”` : 'A device',
            content: first ? (
                <>
                    <p>
                        Each row is one router: serial <span className="pt-data">{first.serial_number}</span>
                        {first.organization_id && <> in org <span className="pt-data">{first.organization_id}</span></>}.
                    </p>
                    {!isMobile && (
                        <p>
                            <b>DATA DIR</b> shows whether collected data exists on the server
                            ({first.has_directory
                                ? <span className="cm-status-chip cm-active">PRESENT</span>
                                : <span className="cm-status-chip cm-inactive">MISSING</span>} for this one).
                        </p>
                    )}
                    <p>
                        <b>ALERTS</b> is{' '}
                        {firstMuted
                            ? <><span className="cm-status-chip cm-inactive">SUPPRESSED</span>: this device won't send notifications.</>
                            : <><span className="cm-status-chip cm-active">ACTIVE</span>: problems on this device trigger notifications.</>}
                    </p>
                </>
            ) : null,
        },
        {
            target: '[data-tour="dm-first-actions"]',
            title: 'Device actions',
            content: (
                <>
                    <p><b>VLAN CAPS / WAN CAPS</b> set data usage limits per VLAN or WAN link.</p>
                    <p><b>{firstMuted ? 'UNMUTE' : 'MUTE'}</b> {firstMuted ? 'turns alerts back on' : 'silences alerts'} for this device (e.g. during maintenance).</p>
                    <p>
                        <b>{isMobile ? 'STOP' : 'STOP MONITORING'}</b> stops collecting but keeps its history.{' '}
                        <b>{isMobile ? 'DELETE' : 'STOP & REMOVE DATA'}</b> also erases stored data, and can't be undone.
                    </p>
                </>
            ),
        },
        {
            target: '[data-tour="dm-archived"]',
            title: 'Archived data',
            content: (
                <p>
                    When you stop monitoring a device, its data folder lands here
                    {archived > 0
                        ? <> (you have <span className="pt-data">{archived}</span> right now)</>
                        : <> (empty for you at the moment)</>}.
                    Use <b>RE-ADD</b> to resume monitoring with history intact, or delete the data to free space.
                </p>
            ),
        },
        replayStep,
    ];
}
